import styled from '@emotion/styled'
import { FC, useState } from 'react'

import { ApiBusinessUnit, ApiClient, ApiNote } from '../../../../../../api/_legacy/generated-api'
import {
  Fees,
  FormItem,
  Space,
  Tabs,
  VerticalAttribute
} from '../../../../../../components/core-v2'
import Icon from '../../../../../../components/core-v2/atoms/Icon'
import IconButton from '../../../../../../components/core-v2/molecules/iconButton'
import LinkIcon from '../../../../../../components/core-v2/molecules/linkIcon'
import { Fee } from '../../../../types'

export type NoteDetailsProps = {
  businessUnit: ApiBusinessUnit
  client: ApiClient
  fees?: Fee[]
  note: ApiNote
  noteTotalValue: number
  onBackToSearch: () => void
  onSelectNote: (note: ApiNote) => void
}

const StyledAttributeCollection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr 1fr 1fr;
`

type TabName = 'details' | 'charges'

export const InspectNote: FC<NoteDetailsProps> = ({
  businessUnit,
  client,
  fees,
  note,
  noteTotalValue,
  onBackToSearch,
  onSelectNote
}) => {
  const [selectedTab, setSelectedTab] = useState<TabName>('details')
  const endDate =
    note.endDate !== undefined
      ? new Date(note.endDate).toLocaleDateString(undefined, {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        })
      : 'Open-ended'
  return (
    <section>
      <Tabs.List className="mt-4 mx-3" spacing="sm">
        <Tabs.Tab
          data-testid="tab-details"
          isSelected={selectedTab === 'details'}
          value="details"
          onClick={() => setSelectedTab('details')}
        >
          Details
        </Tabs.Tab>
        <Tabs.Tab
          data-testid="tab-charges"
          isSelected={selectedTab === 'charges'}
          value="charges"
          onClick={() => setSelectedTab('charges')}
        >
          Charges
        </Tabs.Tab>
      </Tabs.List>
      {selectedTab === 'details' && (
        <Tabs.Panel className="mx-5 my-4" value="details">
          <StyledAttributeCollection data-private>
            <VerticalAttribute name="Client">{client?.clientName}</VerticalAttribute>
            <VerticalAttribute name="Note ID (Stock code)">{note.noteId}</VerticalAttribute>
            <VerticalAttribute name="Business unit">
              {businessUnit?.businessUnitName}
            </VerticalAttribute>
            <VerticalAttribute name="Start date">
              {new Date(note.startDate).toLocaleDateString(undefined, {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </VerticalAttribute>
            <VerticalAttribute name="Note type">{note.taxClassification}</VerticalAttribute>
            <VerticalAttribute name="Maturity date">{endDate}</VerticalAttribute>
            <VerticalAttribute name="Total value of note">{noteTotalValue}</VerticalAttribute>
            <VerticalAttribute name="Dividend processing">
              {note.dividendProcessing}
            </VerticalAttribute>
            <VerticalAttribute name="Collateral for loan">
              {note.isCollateralForLoan ? 'Yes' : 'No'}
            </VerticalAttribute>
          </StyledAttributeCollection>
        </Tabs.Panel>
      )}

      {selectedTab === 'charges' && fees && (
        <Tabs.Panel className="mx-5 my-6" value="charges">
          <FormItem>
            <Fees fees={fees} title="Note charges" />
          </FormItem>
        </Tabs.Panel>
      )}

      <footer className="modal-footer">
        <Space isflex alignItems="center" justifyContent="space-between" mx={5} my={4}>
          <LinkIcon
            disabled={false}
            icon={<Icon name="chevronLeft" size="small" />}
            iconPosition="left"
            text="Back to search"
            onClick={() => onBackToSearch()}
          />
          <IconButton
            data-testid="choose-existing-note"
            disabled={false}
            icon={<Icon name="chevronRight" size="small" />}
            iconPosition="right"
            operationType="progress"
            text="Choose Note"
            onClick={() => onSelectNote(note)}
          />
        </Space>
      </footer>
    </section>
  )
}
